import classNames from 'classnames';
import { uniqueId } from 'lodash';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactResizeDetector from 'react-resize-detector';

import Analytics from '../../../../analytics/Analytics';
import { modelOf } from '../../../../prop-types';
import AdStore from '../../../../store/AdStore';
import ConfigStore from '../../../../store/ConfigStore';
import UIStore from '../../../../store/UIStore';
import RequestState from '../../../../types/RequestState';
import AdZones from '../../../../types/AdZones';
import CommonSlider from '../../../common/Slider';
import AdLoaderSkeleton from '../../../skeleton/AdLoaderSkeleton';
import AdLoaded from '../../AdLoaded';
import AdLoader from '../../AdLoader';
import Banner from '../../Banner';
import {
  filterVisibleBannerSlides,
  getLazyLoadHeight,
  measureBannerImageIntrinsicSize,
  resolveBannerCenterPadding,
  shouldLoopNonInfiniteBannerToStart,
} from '../../bannerMedia';
import BannerSliderStyles, {
  countMainAxisSize,
  countMinCrossAxisSize,
} from '../BannerSliderStyles';
import SSRAwareLazyLoad from '../../../common/SSRAwareLazyLoad';

const BannerSlider = ({
  adStore,
  configStore,
  uiStore,
  analytics,
  className,
  searchParams,
  maximumCrossAxisSize,
  overrideSettings,
  preLoadedAds,
  aspectRatio,
  adZone,
  slideContentJustify,
  useCreativeSlideWidth = false,
}) => {
  // Same as default to sync after change effect to reset slider.
  const AUTOPLAY_SPEED = 3000;
  const MAX_FULL_IMAGE_WIDTH = 1900;
  const SLICK_DEFAULT_CENTER_PADDING = 50;
  const id = useCallback(uniqueId('BannerSlider--'), []);
  const notVariableWidth = useCallback(
    (slides) =>
      !slides.every((slide) => slide.image_width >= MAX_FULL_IMAGE_WIDTH),
    []
  );

  const getSettings = useCallback(
    (slides = []) => {
      const slideCount = slides.length;

      const effectiveSettings = {
        arrows: true,
        autoplay: true,
        autoplaySpeed: AUTOPLAY_SPEED,
        centerMode: false,
        draggable: true,
        swipeToSlide: true,
        variableWidth: notVariableWidth(slides),
        infinite: false,
        touchThreshold: 40,
        ...overrideSettings,
        // Keep all slide content mounted.
        lazyLoad: false,
      };

      // Full-bleed when !centerMode. BOTH (centerMode) uses peek padding so
      // neighbouring slides fill the sides (not empty list background).
      effectiveSettings.centerPadding = resolveBannerCenterPadding(
        effectiveSettings.centerMode,
        overrideSettings,
        `${SLICK_DEFAULT_CENTER_PADDING}px`
      );

      // Peek centering needs variableWidth + explicit slide widths. Full-bleed
      // creatives (>=1900px) otherwise force variableWidth false and break peeks.
      if (effectiveSettings.centerMode && slideCount > 1) {
        effectiveSettings.variableWidth = true;
      }

      if (effectiveSettings.vertical) {
        effectiveSettings.slidesToShow = Math.min(
          slideCount,
          effectiveSettings.slidesToShow
        );
      }

      return effectiveSettings;
    },
    [notVariableWidth, overrideSettings]
  );

  const bannerSliderRef = useRef(null);
  const placeholderRef = useRef(null);
  const sliderRef = useRef(null);
  const resetSlideResetTimeoutIDRef = useRef(null);
  const activePlayingBannerIdRef = useRef(null);
  const autoplayPausedByVideoRef = useRef(false);
  const pendingAutoplayResumeRef = useRef(false);
  const isMidTransitionRef = useRef(false);
  const currentSlideIndexRef = useRef(null);

  const [sentPromotionEvents, setSentPromotionEvents] = useState([]);
  const [elementWidth, setElementWidth] = useState(0);
  const [hasMeasuredSlider, setHasMeasuredSlider] = useState(false);
  // Intrinsic sizes for ads with null API image_width/height (e.g. SVG).
  const [measuredImageSizes, setMeasuredImageSizes] = useState({});

  const clearSlideResetTimeout = () => {
    clearTimeout(resetSlideResetTimeoutIDRef.current);
    resetSlideResetTimeoutIDRef.current = null;
  };

  const pauseVisibleFileVideos = () => {
    if (!bannerSliderRef.current) {
      return;
    }

    bannerSliderRef.current.querySelectorAll('video').forEach((video) => {
      if (!video.paused) {
        video.pause();
      }
    });
  };

  const resumeAutoplayIfNeeded = (slides) => {
    if (
      !getSettings(slides).autoplay ||
      slides.length <= 1 ||
      !sliderRef.current?.slickPlay
    ) {
      return;
    }

    sliderRef.current.slickPlay();
  };

  const handleVideoPlaybackChange = (slides, { bannerId, isPlaying }) => {
    if (isPlaying) {
      activePlayingBannerIdRef.current = bannerId;
      autoplayPausedByVideoRef.current = true;
      pendingAutoplayResumeRef.current = false;
      clearSlideResetTimeout();
      sliderRef.current?.slickPause?.();
      return;
    }

    if (activePlayingBannerIdRef.current !== bannerId) {
      return;
    }

    const wasPausedByVideo = autoplayPausedByVideoRef.current;
    activePlayingBannerIdRef.current = null;
    autoplayPausedByVideoRef.current = false;

    if (!wasPausedByVideo) {
      return;
    }

    if (isMidTransitionRef.current) {
      pendingAutoplayResumeRef.current = true;
    } else {
      resumeAutoplayIfNeeded(slides);
    }

    if (!getSettings().infinite) {
      setSlideResetTimeout(slides.length, currentSlideIndexRef.current);
    }
  };

  useEffect(() => {
    return () => {
      clearSlideResetTimeout();
    };
  }, []);

  // LINK/embed slides have no intrinsic size until slot CSS applies. Remeasure
  // variableWidth after the slider width is known so those slides stay on-track.
  // Same after measured SVG sizes change slide widths (BOTH peeks).
  useEffect(() => {
    if (elementWidth <= 0 || !sliderRef.current) {
      return;
    }

    const innerSlider = sliderRef.current.innerSlider;
    if (innerSlider && typeof innerSlider.onWindowResized === 'function') {
      innerSlider.onWindowResized();
    }
  }, [elementWidth, measuredImageSizes]);

  const handleBannerImageMeasured = ({ bannerId, img }) => {
    const measured = measureBannerImageIntrinsicSize(img);
    if (!measured) {
      return;
    }

    setMeasuredImageSizes((prev) => {
      const existing = prev[bannerId];
      if (
        existing &&
        existing.width === measured.width &&
        existing.height === measured.height
      ) {
        return prev;
      }
      return {
        ...prev,
        [bannerId]: measured,
      };
    });
  };

  const handleResize = () => {
    if (!bannerSliderRef.current) {
      return null;
    }

    const { clientWidth } = bannerSliderRef.current;
    if (clientWidth > 0) {
      if (elementWidth !== clientWidth) {
        setElementWidth(clientWidth);
      }
      setHasMeasuredSlider(true);
    }
  };

  const interactionStartHandler = () => {
    !getSettings().infinite && clearSlideResetTimeout();
  };

  const interactionEndHandler = (slideCount) => {
    !getSettings().infinite &&
      !activePlayingBannerIdRef.current &&
      setSlideResetTimeout(slideCount, currentSlideIndexRef.current);
  };

  const setSlideResetTimeout = (slideCount, currentSlide) => {
    const slideIndex =
      currentSlide !== undefined && currentSlide !== null
        ? currentSlide
        : currentSlideIndexRef.current;

    if (
      !shouldLoopNonInfiniteBannerToStart(slideCount, slideIndex) ||
      activePlayingBannerIdRef.current
    ) {
      return;
    }

    clearSlideResetTimeout();
    resetSlideResetTimeoutIDRef.current = setTimeout(() => {
      if (sliderRef.current) {
        sliderRef.current.slickGoTo(0);
      }
    }, AUTOPLAY_SPEED);
  };

  const sendPromotionView = (slideIndex, slides) => {
    const banner = slides[slideIndex];

    if (banner && sentPromotionEvents.indexOf(slideIndex) === -1) {
      const bannerZone = adZone;
      analytics.viewPromotion([
        {
          bannerZone,
          banner,
        },
      ]);

      setSentPromotionEvents([...sentPromotionEvents, slideIndex]);
    }
  };

  const onSliderInit = (slides) => {
    slides.length > 0 && sendPromotionView(0, slides);
  };

  const onBannerAfterChange = (slides, currentSlide) => {
    const slideCount = slides.length;
    slideCount > 0 && sendPromotionView(currentSlide, slides);

    isMidTransitionRef.current = false;
    currentSlideIndexRef.current = currentSlide;

    if (pendingAutoplayResumeRef.current) {
      pendingAutoplayResumeRef.current = false;
      resumeAutoplayIfNeeded(slides);
    }

    !getSettings().infinite &&
      !activePlayingBannerIdRef.current &&
      setSlideResetTimeout(slideCount, currentSlide);
  };

  const onBannerBeforeChange = () => {
    isMidTransitionRef.current = true;
    !getSettings().infinite && clearSlideResetTimeout();

    if (activePlayingBannerIdRef.current) {
      clearSlideResetTimeout();
      pauseVisibleFileVideos();
    }
  };

  const getSliderEvents = (slides) => {
    return {
      beforeChange: onBannerBeforeChange,
      afterChange: (currentSlide) => onBannerAfterChange(slides, currentSlide),
      onInit: () => onSliderInit(slides),
    };
  };

  const getScentHeight = () => {
    if (adZone !== AdZones.MAIN_BANNER && adZone !== AdZones.FOOTER_BANNER) {
      return 0;
    }

    return configStore.getScentHeight(uiStore.isMobile);
  };

  const getMaximumDefaultHeight = () => {
    const parentHeight =
      bannerSliderRef.current && elementWidth > 0
        ? countMainAxisSize(elementWidth, aspectRatio)
        : maximumCrossAxisSize;

    return (
      countMinCrossAxisSize(maximumCrossAxisSize, parentHeight) +
      getScentHeight()
    );
  };

  const getSlides = (ads) => {
    return filterVisibleBannerSlides(ads, uiStore.isSmallest);
  };

  // Mobile/desktop image swap needs a fresh intrinsic measure.
  useEffect(() => {
    setMeasuredImageSizes({});
  }, [uiStore.isSmallest]);

  /**
   * Widest a slide may become. react-slick sizes the track as slide count
   * times this width, so a wider (floated) slide wraps onto a second row and
   * breaks slide positioning. Mirrors react-slick's own slide width math,
   * where center mode reserves padding for neighbouring slides when
   * centerPadding is non-zero.
   */
  const getMaximumSlideWidth = (slides) => {
    if (!elementWidth) {
      return 0;
    }

    const settings = getSettings(slides);
    const centerPadding =
      settings.centerMode && slides.length > 1
        ? parseInt(
            settings.centerPadding || `${SLICK_DEFAULT_CENTER_PADDING}px`,
            10
          ) * 2
        : 0;

    return Math.floor(
      (elementWidth - centerPadding) / (settings.slidesToShow || 1)
    );
  };

  const getAds = () => (ads) => {
    if (!ads || ads.length === 0) {
      return null;
    }

    const slides = getSlides(ads);
    const sliderEvents = getSliderEvents(slides);

    return (
      <div
        className={classNames('BannerSlider', className)}
        id={id}
        ref={bannerSliderRef}
        onMouseEnter={interactionStartHandler}
        onMouseLeave={() => interactionEndHandler(slides.length)}
        onTouchStart={interactionStartHandler}
        onTouchEnd={() => interactionEndHandler(slides.length)}
      >
        <CommonSlider
          ref={sliderRef}
          {...getSettings(slides)}
          {...sliderEvents}
        >
          {slides.map((banner) => {
            return (
              <div key={banner.id} className="BannerSlider__slide">
                <Banner
                  banner={banner}
                  bannerZone={adZone}
                  onVideoPlaybackChange={(payload) =>
                    handleVideoPlaybackChange(slides, payload)
                  }
                  onImageMeasured={handleBannerImageMeasured}
                />
              </div>
            );
          })}
        </CommonSlider>
        <ReactResizeDetector
          handleWidth
          onResize={handleResize}
          refreshMode="throttle"
          refreshRate={1000}
        />
        <BannerSliderStyles
          bannerSliderId={id}
          slides={slides}
          elementWidth={elementWidth}
          maximumSlideWidth={getMaximumSlideWidth(slides)}
          useCreativeSlideWidth={useCreativeSlideWidth && slides.length > 1}
          measuredImageSizes={measuredImageSizes}
          bannerSlider={bannerSliderRef.current}
          aspectRatio={aspectRatio}
          maximumCrossAxisSize={maximumCrossAxisSize}
          adZone={adZone}
          ifVertical={getSettings().vertical}
          slidesToShow={getSettings(slides).slidesToShow}
          slideContentJustify={slideContentJustify}
        />
      </div>
    );
  };

  if (preLoadedAds && preLoadedAds.length > 0) {
    return <AdLoaded ads={preLoadedAds}>{getAds()}</AdLoaded>;
  }

  const ads = adStore.getAdsByAdZone(searchParams, adZone);
  if (
    adStore.getAdQueryStateBySearchParameters(searchParams) ===
      RequestState.LOADED &&
    ads?.length === 0
  ) {
    return null;
  }

  return (
    <SSRAwareLazyLoad
      once
      offset={50}
      height={getLazyLoadHeight(hasMeasuredSlider, getMaximumDefaultHeight())}
      ref={placeholderRef}
    >
      <AdLoader
        placeHolder={<AdLoaderSkeleton />}
        searchParams={searchParams}
        adZone={adZone}
      >
        {getAds()}
      </AdLoader>
    </SSRAwareLazyLoad>
  );
};

BannerSlider.propTypes = {
  adStore: modelOf(AdStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  uiStore: modelOf(UIStore).isRequired,
  analytics: PropTypes.instanceOf(Analytics).isRequired,
  aspectRatio: PropTypes.number.isRequired,
  searchParams: PropTypes.object.isRequired,
  adZone: PropTypes.string.isRequired,
  preLoadedAds: PropTypes.array,
  className: PropTypes.string,
  // Cross axis size here refers to either maximum height (if the slider is
  // horizontal) or maximum width (if the slider is vertical).
  maximumCrossAxisSize: PropTypes.number,
  overrideSettings: PropTypes.object,
  // flex-start = INFINITE (left); center = CENTERED / BOTH.
  slideContentJustify: PropTypes.oneOf(['flex-start', 'center']),
  // BOTH peeks: size .slick-slide to the creative when there are multiple slides.
  useCreativeSlideWidth: PropTypes.bool,
};

BannerSlider.defaultProps = {
  maximumCrossAxisSize: 400,
  slideContentJustify: 'center',
  useCreativeSlideWidth: false,
};

export default inject(
  'adStore',
  'configStore',
  'uiStore',
  'analytics'
)(observer(BannerSlider));
